import { Component } from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';

import { uploadService } from '@services/uploadService.ts';
import type { UploadItem } from '@services/uploadService.ts';
import { fileService } from '@services/fileService.ts';
import { DragDropZone } from '@services/dragDropService.ts';
import { ImgUpload } from './Images/ImgUpload';
import { ImgClose } from './Images/ImgClose.tsx';

@observer
export class UploadPanel extends Component<{ folderId?: string | null; enabled: boolean }> {
  @observable private _isDragOver = false;
  @observable private _urlInput = '';
  @observable private _showUrlInput = false;
  private _dragZone: DragDropZone;

  constructor(props: { folderId?: string | null; enabled: boolean }) {
    super(props);
    makeObservable(this);
    this._dragZone = new DragDropZone({
      isEnabled: () => this.props.enabled,
      onActiveChange: (active) => { this._isDragOver = active; },
      onDrop: (files, text) => this._handleDrop(files, text),
    });
  }

  componentDidMount() {
    document.addEventListener('paste', this._onPaste);
    this._dragZone.attach();
  }

  componentWillUnmount(): void {
    document.removeEventListener('paste', this._onPaste);
    this._dragZone.detach();
  }

  render() {
    const uploads = uploadService.uploads;

    return (
      <>
        {/* Full-page Drop Zone Overlay — drag events are handled at the window
            level by DragDropZone, so this element only reflects the state. */}
        <div
          className={`dropzone-overlay ${this._isDragOver ? 'active' : ''}`}
        >
          <div className="dropzone-content">
            <div className="icon"><ImgUpload size={64} /></div>
            <h2>Drop anywhere!</h2>
            <p>Release to upload your files</p>
          </div>
        </div>

        {/* URL Input Bar */}
        {this._showUrlInput && (
          <div className="upload-url-bar">
            <input
              type="text"
              className="form-control"
              placeholder="Paste a URL to download..."
              value={this._urlInput}
              onChange={this._onUrlInputChange}
              onKeyDown={this._onUrlKeyDown}
              autoFocus
            />
            <button className="btn btn-primary" onClick={this._onUrlSubmit} disabled={!this._urlInput.trim()}>
              Download
            </button>
            <button className="btn btn-secondary" onClick={this._onUrlCancel}>
              Cancel
            </button>
          </div>
        )}

        {/* Upload Progress Panel */}
        {uploads.length > 0 && (
          <div className="upload-panel">
            <div className="upload-panel-header">
              <span className="upload-panel-title">
                {this._getHeaderText(uploads)}
              </span>
              {uploads.some(u => u.status === 'done' || u.status === 'error') && (
                <button className="btn btn-secondary upload-panel-clear" onClick={this._onClearCompleted}>
                  Clear
                </button>
              )}
            </div>
            <div className="upload-panel-list">
              {uploads.map(item => (
                <div className={`upload-item upload-item-${item.status}`} key={item.id}>
                  <div className="upload-item-info">
                    <span className="upload-item-name" title={item.fileName}>{item.fileName}</span>
                    <span className="upload-item-meta">
                      {this._getItemMeta(item)}
                    </span>
                  </div>
                  <div className="upload-item-progress-bar">
                    <div
                      className={`upload-item-progress-fill upload-status-${item.status}`}
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                  <div className="upload-item-actions">
                    {(item.status === 'uploading' || item.status === 'processing') && (
                      <button className="action-btn" title="Cancel" onClick={() => uploadService.cancelUpload(item.id)}>
                        <ImgClose />
                      </button>
                    )}
                    {(item.status === 'done' || item.status === 'error') && (
                      <button className="action-btn" title="Remove" onClick={() => uploadService.removeUpload(item.id)}>
                        <ImgClose />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  }

  private _getHeaderText(uploads: UploadItem[]): string {
    const active = uploads.filter(u => u.status === 'uploading' || u.status === 'processing');
    const done = uploads.filter(u => u.status === 'done');
    if (active.length > 0) {
      return `Uploading ${active.length} file${active.length > 1 ? 's' : ''}...`;
    }
    if (done.length === uploads.length) {
      return `${done.length} upload${done.length > 1 ? 's' : ''} complete`;
    }
    return 'Uploads';
  }

  private _getItemMeta(item: UploadItem): string {
    if (item.status === 'error') {
      return item.error || 'Error';
    }
    if (item.status === 'done') {
      return fileService.formatFileSize(item.fileSize);
    }
    const parts: string[] = [];
    parts.push(`${fileService.formatFileSize(item.loaded)} / ${fileService.formatFileSize(item.fileSize)}`);
    if (item.bitrate) {
      parts.push(uploadService.formatBitrate(item.bitrate));
    }
    if (item.timeLeftSeconds) {
      parts.push(uploadService.formatTimeLeft(item.timeLeftSeconds));
    }
    return parts.join(' · ');
  }

  // --- File input trigger ---
  public triggerFileInput() {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = () => {
      if (input.files && input.files.length > 0) {
        uploadService.uploadFiles(input.files, this.props.folderId);
      }
    };
    input.click();
  }

  public showUrlInput() {
    this._showUrlInput = true;
  }

  // --- Drag & Drop ---
  private _handleDrop(files: FileList, text: string): void {
    if (files.length > 0) {
      uploadService.uploadFiles(files, this.props.folderId);
    }

    // Check for URLs in dropped text
    if (text && (text.startsWith('http://') || text.startsWith('https://'))) {
      uploadService.uploadFromUrl(text, this.props.folderId);
    }
  }

  // --- Clipboard Paste ---
  private _onPaste = (e: ClipboardEvent) => {
    // Don't handle paste if user is typing in an input/textarea
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
      // Allow paste in the URL input
      if (!active.classList.contains('upload-url-input')) {
        return;
      }
    }

    const items = e.clipboardData?.items;
    if (!items) return;

    // Check for image files
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          const ext = file.type.split('/')[1] || 'png';
          const name = `pasted-image-${Date.now()}.${ext}`;
          uploadService.uploadBlob(file, name, this.props.folderId);
          return;
        }
      }
    }

    // Check for pasted URL text
    const text = e.clipboardData?.getData('text/plain');
    if (text && (text.startsWith('http://') || text.startsWith('https://'))) {
      // Only auto-upload URLs if not in an input field
      if (!active || (active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA')) {
        e.preventDefault();
        uploadService.uploadFromUrl(text, this.props.folderId);
      }
    }
  };

  // --- URL Input handlers ---
  @action.bound
  private _onUrlInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    this._urlInput = e.target.value;
  }

  @action.bound
  private _onUrlKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && this._urlInput.trim()) {
      this._onUrlSubmit();
    }
    if (e.key === 'Escape') {
      this._onUrlCancel();
    }
  }

  @action.bound
  private _onUrlSubmit() {
    const url = this._urlInput.trim();
    if (url) {
      uploadService.uploadFromUrl(url, this.props.folderId);
      this._urlInput = '';
      this._showUrlInput = false;
    }
  }

  @action.bound
  private _onUrlCancel() {
    this._urlInput = '';
    this._showUrlInput = false;
  }

  @action.bound
  private _onClearCompleted() {
    uploadService.clearCompleted();
  }
}
